import Swal from "sweetalert2";
import "./swal-globals.css";
import checkIcon from "../../img/checkIcon.svg";

// Inline custom styles for SweetAlert2 notification modals. Matches the
// frosted-panel + soft-icon language used across the metric/KPI modal family
// (see CompanyMetrics/constants.js FROSTED_PANEL_STYLE and
// MetricSettingsModal's inline "Appearance updated" success panel).
const notificationCss = `
  .custom-notification-bg.swal2-popup {
    border-radius: 20px !important;
    background: linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.84) 100%) !important;
    box-shadow: 0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7) !important;
    padding: 2rem 1.75rem 1.75rem !important;
    width: 100% !important;
    max-width: 380px !important;
    backdrop-filter: blur(30px) saturate(185%) !important;
    -webkit-backdrop-filter: blur(30px) saturate(185%) !important;
    border: 1px solid rgba(255,255,255,0.6) !important;
  }
  /* Quicker than the old defaults (300ms/150ms) — the app's own network
     latency was making it read as "slow to show" — but eased enough to
     still feel like a transition rather than a jump cut. */
  .custom-notification-bg.swal2-show {
    animation-duration: 240ms !important;
  }
  .custom-notification-bg.swal2-hide {
    animation-duration: 160ms !important;
  }
  .swal-container .swal2-confirm {
    background: var(--gmb-primary, #04549C) !important;
    color: #fff !important;
    border: none !important;
    box-shadow: 0 1px 2px rgba(16,24,40,0.06) !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    text-transform: none !important;
    border-radius: 10px !important;
    outline: none !important;
    padding: 10px 28px !important;
    transition: filter 0.15s ease, transform 0.1s ease;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    text-align: center;
  }
  .swal-container .swal2-confirm:hover {
    filter: brightness(0.95);
  }
  .swal-container .swal2-confirm:active {
    transform: scale(0.98);
  }
  .swal-container .swal2-confirm:focus {
    outline: 2px solid var(--gmb-primary, #344054) !important;
    outline-offset: 2px !important;
  }
  .custom-notification-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #ECFDF5;
    color: #10B981;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }
  /* swal-globals.css sets the base .swal2-title / .swal2-html-container
     look app-wide; this popup renders its own heading inside the html
     string instead (so icon → title → body can match the rest of the
     KPI modal family's success-panel order), so hide the default title. */
  .custom-notification-bg .swal2-title {
    display: none !important;
  }
  .custom-notification-bg .swal2-html-container {
    margin: 0 !important;
    padding: 0 !important;
  }
  .custom-notification-title {
    font-family: 'DM Sans', Arial, Helvetica, sans-serif !important;
    font-weight: 600 !important;
    font-size: 17px !important;
    line-height: 24px !important;
    letter-spacing: -0.2px !important;
    color: #0F172A !important;
    margin: 0 0 4px !important;
    display: block;
  }
  .custom-notification-text {
    font-family: 'DM Sans', Arial, Helvetica, sans-serif !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #64748B !important;
    margin-bottom: 0;
    display: block;
  }
  .custom-notification-bg .swal2-actions {
    padding: 0 !important;
    margin-top: 20px !important;
    width: 100%;
  }
`;

const styleTagId = "custom-notification-modal-css";
if (!document.getElementById(styleTagId)) {
  const styleTag = document.createElement("style");
  styleTag.id = styleTagId;
  styleTag.textContent = notificationCss;
  document.head.appendChild(styleTag);
}

// Flat, single-stroke checkmark in a soft emerald tile — same iconography the
// rest of the KPI modal family already uses for a success state, in place of
// the old oversized gradient-ring badge image.
const successIconHtml = `
  <div class="custom-notification-icon">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  </div>
`;

/**
 * Pops a custom notification modal matching the metric/KPI modal family's
 * frosted-panel style.
 *
 * @param {Object} param0
 * @param {string} param0.title
 * @param {string} [param0.text]
 * @param {boolean} [param0.compact] Smaller rounded toast-style modal
 */
const popNotification = ({ title, text = "", compact = false }) => {
  if (compact) {
    Swal.fire({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: false,
      backdrop: false,
      html: `
        <div class="gmb-save-toast-inner">
          <img class="gmb-save-toast-icon" src="${checkIcon}" alt="" />
          <div>
            <span class="gmb-save-toast-title">${title}</span>
            ${text ? `<span class="gmb-save-toast-text">${text}</span>` : ""}
          </div>
        </div>
      `,
      customClass: {
        container: "swal-container",
        popup: "gmb-save-toast",
      },
      allowOutsideClick: true,
      allowEscapeKey: true,
    });
    return;
  }

  Swal.fire({
    html: `${successIconHtml}<span class="custom-notification-title">${title}</span>${
      text ? `<span class="custom-notification-text">${text}</span>` : ""
    }`,
    showConfirmButton: true,
    confirmButtonText: "OK",
    customClass: {
      container: "swal-container",
      popup: "custom-notification-bg",
      title: "custom-notification-title",
      htmlContainer: "",
    },
    allowOutsideClick: true,
    allowEscapeKey: true,
  });
};

export default popNotification;
